import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	useChatContextAIFeatureContext,
	useChatContextArtifactContext,
	useChatContextDatabaseContext,
	useChatContextEditorContext,
} from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

import { useChatModeStoreActions } from '../../../../controllers/chat-mode-store/useChatModeStoreActions';

import { PageContextType } from './PageContextType';
import type { JiraViewContextData, TrelloViewContextData, CurrentPageContextType } from './types';
import { usePageContextType } from './usePageContextType';

const JIRA_VIEW_CONTEXT_KEY = 'jira_view_context';
const TRELLO_VIEW_CONTEXT_KEY = 'trello_view_context';

function hasValidJiraViewContextData(value: unknown): value is JiraViewContextData {
	if (value == null || typeof value !== 'object') {
		return false;
	}
	const selected = Object.getOwnPropertyDescriptor(value, 'selectedIssueIdentifiers')?.value;
	return Array.isArray(selected);
}

function hasValidTrelloViewContextData(value: unknown): value is TrelloViewContextData {
	if (value == null || typeof value !== 'object') {
		return false;
	}
	const url = Object.getOwnPropertyDescriptor(value, 'url')?.value;
	return typeof url === 'string';
}

export const useCurrentPageAsContext = (): {
	canUseCurrentPageContextUserPreference: (key: string) => boolean;
	setUseCurrentPageContextUserPreference: (key: string, isEnabled: boolean) => void;
	currentContext: CurrentPageContextType;
	jiraViewContextData: JiraViewContextData | undefined;
} => {
	const pageContextType = usePageContextType();
	const { getChatMode, setUseCurrentPageContext } = useChatModeStoreActions();
	const [editorContext] = useChatContextEditorContext();
	const [artifactContext] = useChatContextArtifactContext();
	const [databaseContext] = useChatContextDatabaseContext();
	const [aiFeatureContext] = useChatContextAIFeatureContext();
	const rawJiraViewContext = aiFeatureContext?.[JIRA_VIEW_CONTEXT_KEY];
	const jiraViewContextData = hasValidJiraViewContextData(rawJiraViewContext)
		? rawJiraViewContext
		: undefined;
	const rawTrelloViewContext = aiFeatureContext?.[TRELLO_VIEW_CONTEXT_KEY];
	const trelloViewContextData = hasValidTrelloViewContextData(rawTrelloViewContext)
		? rawTrelloViewContext
		: undefined;

	let currentContext: CurrentPageContextType = {
		type: 'none',
	};

	if (trelloViewContextData && fg('trello_enterprise_rovo_trello_context')) {
		// prefer trello context over page context if available
		currentContext = {
			type: 'trello_view_context',
			payload: trelloViewContextData,
		};
	} else if (artifactContext) {
		currentContext = {
			type: 'artifact_context',
			payload: artifactContext,
		};
	} else if (editorContext) {
		// prefer editor context over page context if available
		currentContext = {
			type: 'editor_context',
			payload: editorContext,
		};
	} else if (databaseContext) {
		currentContext = {
			type: 'database_context',
			selectedElementIds: databaseContext.selectedElementIds ?? [],
		};
	} else if (
		jiraViewContextData &&
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue(
			'jira-ai-jira-view-context-in-rovo-chat-experiment',
			'isEnabled',
			false,
		)
	) {
		currentContext = {
			type: 'jira_view_context',
			payload: jiraViewContextData,
		};
	} else if (pageContextType !== PageContextType.UNKNOWN) {
		// if no editor context, use page context if available
		currentContext = {
			type: 'current_page',
			pageContextType,
		};
	}

	return {
		canUseCurrentPageContextUserPreference: (key: string): boolean => {
			return getChatMode(key).useCurrentPageContext;
		},
		setUseCurrentPageContextUserPreference: setUseCurrentPageContext,
		currentContext,
		jiraViewContextData,
	};
};
